import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Search, FolderKanban, ArrowRight, Trash2, Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { getProjects, createProject, deleteProject } from "../lib/api";

const ASIL_LEVELS = ["QM", "ASIL A", "ASIL B", "ASIL C", "ASIL D"];
const TEST_LEVELS = ["Unit", "Integration", "System", "SIL", "MIL", "HIL", "Vehicle"];

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", vehicle_platform: "", ecu: "",
    sw_version: "", hpcu_sw: "", baseline_sw: "", vehicle_code: "",
    asil_level: "", test_level: "",
  });

  const fetchProjects = () => {
    setLoading(true);
    getProjects({ search: search || undefined })
      .then((res) => setProjects(res.data))
      .catch(() => toast.error("Failed to load projects"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProjects();
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    try {
      const res = await createProject(form);
      toast.success("Project created");
      setDialogOpen(false);
      setForm({ name: "", description: "", vehicle_platform: "", ecu: "", sw_version: "", hpcu_sw: "", baseline_sw: "", vehicle_code: "", asil_level: "", test_level: "" });
      navigate(`/projects/${res.data.id}`);
    } catch {
      toast.error("Failed to create project");
    }
  };

  const handleDelete = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this project and all associated data?")) return;
    try {
      await deleteProject(id);
      toast.success("Project deleted");
      fetchProjects();
    } catch {
      toast.error("Failed to delete project");
    }
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#003057]" data-testid="projects-title">
            Projects
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage test projects and configurations
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-project-btn"
              className="bg-[#003057] hover:bg-[#002545] text-white rounded-sm"
            >
              <Plus className="w-4 h-4 mr-1" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg" data-testid="create-project-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#003057]">Create New Project</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">
                Configure a new test project with vehicle and safety parameters.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Project Name *
                </Label>
                <Input
                  data-testid="project-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., ADAS Braking System V2.1"
                  className="mt-1 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Description
                </Label>
                <Textarea
                  data-testid="project-desc-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief project description"
                  className="mt-1 rounded-sm"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Vehicle Platform
                  </Label>
                  <Input
                    data-testid="project-platform-input"
                    value={form.vehicle_platform}
                    onChange={(e) => setForm({ ...form, vehicle_platform: e.target.value })}
                    placeholder="e.g., SPA2"
                    className="mt-1 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    ECU
                  </Label>
                  <Input
                    data-testid="project-ecu-input"
                    value={form.ecu}
                    onChange={(e) => setForm({ ...form, ecu: e.target.value })}
                    placeholder="e.g., VDCM"
                    className="mt-1 rounded-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    SW Version
                  </Label>
                  <Input
                    data-testid="project-sw-input"
                    value={form.sw_version}
                    onChange={(e) => setForm({ ...form, sw_version: e.target.value })}
                    placeholder="v2.1.0"
                    className="mt-1 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    HPCU S/W
                  </Label>
                  <Input
                    data-testid="project-hpcu-input"
                    value={form.hpcu_sw}
                    onChange={(e) => setForm({ ...form, hpcu_sw: e.target.value })}
                    placeholder="e.g., HPCU 4.1"
                    className="mt-1 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Baseline S/W
                  </Label>
                  <Input
                    data-testid="project-baseline-input"
                    value={form.baseline_sw}
                    onChange={(e) => setForm({ ...form, baseline_sw: e.target.value })}
                    placeholder="e.g., Baseline T0"
                    className="mt-1 rounded-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Vehicle Code
                  </Label>
                  <Input
                    data-testid="project-vehicle-code-input"
                    value={form.vehicle_code}
                    onChange={(e) => setForm({ ...form, vehicle_code: e.target.value })}
                    placeholder="e.g., B-1927"
                    className="mt-1 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    ASIL Level
                  </Label>
                  <Select
                    value={form.asil_level}
                    onValueChange={(v) => setForm({ ...form, asil_level: v })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="project-asil-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {ASIL_LEVELS.map((l) => (
                        <SelectItem key={l} value={l}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Test Level
                  </Label>
                  <Select
                    value={form.test_level}
                    onValueChange={(v) => setForm({ ...form, test_level: v })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="project-level-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {TEST_LEVELS.map((l) => (
                        <SelectItem key={l} value={l}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-project-btn"
                onClick={handleCreate}
                className="bg-[#003057] hover:bg-[#002545] text-white rounded-sm"
              >
                Create Project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex items-center gap-2" data-testid="search-form">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects, ECUs..."
            className="pl-9 rounded-sm"
          />
        </div>
        <Button type="submit" variant="outline" className="rounded-sm border-slate-300">
          <Filter className="w-4 h-4 mr-1" /> Filter
        </Button>
      </form>

      {/* Project Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 bg-slate-200 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16" data-testid="no-projects">
          <FolderKanban className="w-12 h-12 mx-auto text-slate-300 mb-4" strokeWidth={1.5} />
          <p className="text-slate-500 text-sm">No projects found</p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="mt-4 bg-[#003057] hover:bg-[#002545] text-white rounded-sm"
          >
            Create your first project
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
          {projects.map((project, i) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              data-testid={`project-card-${project.id}`}
            >
              <Card className="bg-white border border-slate-200 rounded-sm shadow-sm hover:shadow-md transition-all duration-200 h-full group">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <h3 className="text-sm font-semibold text-[#003057] group-hover:text-[#266497] transition-colors truncate pr-2">
                      {project.name}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`delete-project-${project.id}`}
                      onClick={(e) => handleDelete(e, project.id)}
                      className="h-6 w-6 p-0 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {project.description && (
                    <p className="text-xs text-slate-500 line-clamp-2">{project.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {project.ecu && (
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-slate-300 text-slate-600">
                        ECU: {project.ecu}
                      </Badge>
                    )}
                    {project.vehicle_platform && (
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-slate-300 text-slate-600">
                        {project.vehicle_platform}
                      </Badge>
                    )}
                    {project.asil_level && (
                      <Badge className="text-[10px] h-5 px-1.5 bg-[#003057]/10 text-[#003057] border-0">
                        {project.asil_level}
                      </Badge>
                    )}
                    {project.test_level && (
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-slate-300 text-slate-600">
                        {project.test_level}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500 pt-1 border-t border-slate-100">
                    <span>{project.file_count || 0} files</span>
                    <span>{project.report_count || 0} reports</span>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
